import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Building2, MapPin, FileText, ExternalLink, Sparkles, Loader2, Star,
  Kanban, Bookmark, ListChecks, Trophy, ArrowRight, Rocket, Bot,
} from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDate, fmtDateTime, errMsg } from "@/lib/api";
import { createPortal } from "react-dom";
import { AderenciaBadge, StageBadge, Chip, ScoutBadge, StatusOpBadge, ComplianceBadge } from "@/components/Badges";
import { BenchmarkHistorico } from "@/components/BenchmarkHistorico";

const STAGES = ["NOVA", "ANALISAR", "FAVORITA", "DOCUMENTAÇÃO", "PROPOSTA EM PREPARAÇÃO", "PROPOSTA ENVIADA", "EM DISPUTA", "GANHA", "PERDIDA", "CANCELADA"];

export default function OpportunityDrawer({ id, onClose, onChanged }) {
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [tab, setTab] = useState("info");
  const [itens, setItens] = useState(null);
  const [loadingItens, setLoadingItens] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [participando, setParticipando] = useState(false);

  const load = () => api.get(`/opportunities/${id}`).then((r) => { setOpp(r.data); setItens(r.data.itens); });
  useEffect(() => { if (id) { setOpp(null); setItens(null); setTab("info"); load(); } }, [id]);

  const loadItens = async () => {
    setLoadingItens(true);
    try {
      const { data } = await api.get(`/opportunities/${id}/itens`);
      setItens(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoadingItens(false); }
  };

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/opportunities/${id}`, body);
      setOpp(data);
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/opportunities/${id}/analyze`);
      setOpp((o) => ({ ...o, ai_analysis: data.ai_analysis }));
      toast.success("Análise concluída pela IA");
    } catch (e) { toast.error(errMsg(e)); } finally { setAnalyzing(false); }
  };

  const scoutFb = async (decisao) => {
    if (decisao === "descartar") {
      const motivo = window.prompt("Motivo do descarte (Produto, Distância, Margem, Documentação, Prazo, Fornecedor, Complexidade, Logística, Outro):", "Outro");
      if (motivo === null) return;
      try { const { data } = await api.post(`/opportunities/${id}/scout/feedback`, { decisao, motivo }); setOpp(data); toast.success("🚫 Descartada"); onChanged && onChanged(); } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    try {
      const { data } = await api.post(`/opportunities/${id}/scout/feedback`, { decisao });
      setOpp(data);
      const msg = { interesse: "👍 Interesse registrado", nao_interesse: "👎 Sem interesse", favoritar: "⭐ Favoritada" };
      toast.success(msg[decisao] || "Registrado");
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleCheck = (idx) => {
    const cl = (opp.checklist || []).map((c, i) => i === idx ? { ...c, done: !c.done } : c);
    patch({ checklist: cl });
  };

  const participar = async () => {
    setParticipando(true);
    try {
      await api.post(`/opportunities/${id}/participar`);
      onChanged && onChanged();
      navigate(`/participar/${id}`);
    } catch (e) { toast.error(errMsg(e)); setParticipando(false); }
  };

  const converter = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/opportunities/${id}/converter-venda`);
      toast.success(`Venda criada — contrato ${data.contrato} e conta a receber gerada`);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setConverting(false); }
  };

  if (!id) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} data-testid="drawer-overlay" />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col bg-slate-50 shadow-2xl animate-fade-up">
        {!opp ? (
          <div className="grid flex-1 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-white p-5">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Chip className="bg-sky-50 text-sky-700">{opp.modalidade_nome}</Chip>
                  <AderenciaBadge value={opp.aderencia} />
                  <StageBadge stage={opp.stage} />
                  {opp.commercially_compatible && <Chip className="bg-emerald-50 text-emerald-700">Comercialmente compatível</Chip>}
                </div>
                <h2 className="font-display text-lg font-bold leading-snug text-slate-900">{opp.orgao_razao_social}</h2>
                <p className="mt-1 line-clamp-2 text-sm text-slate-500">{opp.objeto}</p>
              </div>
              <button onClick={onClose} data-testid="drawer-close" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex gap-1 border-b border-slate-200 bg-white px-4">
              {[
                { id: "info", label: "Informações" },
                { id: "itens", label: "Itens" },
                { id: "gestao", label: "Gestão" },
                { id: "ia", label: "IA" },
              ].map((t) => (
                <button key={t.id} data-testid={`drawer-tab-${t.id}`} onClick={() => { setTab(t.id); if (t.id === "itens" && !itens) loadItens(); }}
                  className={`border-b-2 px-3 py-2.5 text-sm font-medium ${tab === t.id ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {tab === "info" && (
                <div className="space-y-4">
                  {opp.scout && (
                    <div className="rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-4" data-testid="drawer-scout-panel">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><Bot className="h-4 w-4 text-sky-600" /> SCOUT analisou</div>
                        <div className="flex items-center gap-2"><ScoutBadge scout={opp.scout} /><StatusOpBadge status={opp.scout.status_operacional} /></div>
                      </div>
                      {opp.compliance_status && <div className="mb-2"><ComplianceBadge status={opp.compliance_status} /></div>}
                      <div className="mb-3 flex items-center gap-3">
                        <div className="relative grid h-14 w-14 place-items-center rounded-full bg-sky-100 text-lg font-bold text-sky-700">{opp.scout.indice}</div>
                        <div className="text-xs text-slate-600">
                          <div><strong>Índice Scout</strong> — priorização interna (0–100). Não representa probabilidade de vitória.</div>
                          {opp.scout.situacao_extra && <div className="mt-0.5 font-semibold text-amber-700">Situação: {opp.scout.situacao_extra}</div>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        {[
                          ["Aderência", opp.scout.aderencia?.nivel],
                          ["Complexidade", opp.scout.complexidade?.nivel],
                          ["Logística", opp.scout.logistica?.distancia_km != null ? `${opp.scout.logistica.distancia_km} km (${opp.scout.logistica.nivel})` : (opp.scout.logistica?.nivel || "—")],
                          ["Prazo", opp.scout.prazo?.dias != null ? `${opp.scout.prazo.dias} dia(s) (${opp.scout.prazo.nivel})` : opp.scout.prazo?.nivel],
                          ["Fornecimento", opp.scout.fornecimento?.nivel],
                          ["Risco", opp.scout.risco?.nivel],
                          ["Histórico", opp.recorrencia?.is ? `♻ recorrente${opp.recorrencia.desconto_medio_pct != null ? ` · desc. ${opp.recorrencia.desconto_medio_pct}%` : ""}` : (opp.recorrencia ? "sem histórico" : "—")],
                        ].map(([l, v]) => (
                          <div key={l} className="rounded-lg border border-slate-200 bg-white p-2"><div className="font-semibold uppercase text-slate-400">{l}</div><div className="mt-0.5 font-medium capitalize text-slate-700">{String(v || "—").replace(/_/g, " ")}</div></div>
                        ))}
                      </div>
                      <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2 text-[11px] text-slate-500">
                        <span className="font-semibold text-slate-600">Local de entrega:</span> {opp.scout.logistica?.local_entrega || "⚪ não identificado"} · {opp.scout.economico?.nota}
                      </div>
                      {(opp.scout.motivos || []).length > 0 && (
                        <div className="mt-2">
                          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">Por que essa oportunidade?</div>
                          <ul className="space-y-0.5">{opp.scout.motivos.map((m, i) => <li key={i} className="flex items-start gap-1 text-[11px] text-slate-600"><span className="text-sky-500">•</span>{m}</li>)}</ul>
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-1" data-testid="drawer-scout-feedback">
                        {[["interesse", "👍 Interesse"], ["favoritar", "⭐ Favoritar"], ["nao_interesse", "👎 Sem interesse"], ["descartar", "🚫 Descartar"]].map(([d, l]) => (
                          <button key={d} data-testid={`drawer-scout-fb-${d}`} onClick={() => scoutFb(d)} className={`flex-1 rounded-lg border px-2 py-1.5 text-[11px] font-semibold ${opp.scout_feedback?.decisao === d ? "border-sky-400 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <BenchmarkHistorico oid={opp.id} />
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ["Órgão", opp.orgao_razao_social], ["CNPJ", opp.orgao_cnpj],
                      ["Unidade", opp.unidade_nome], ["Processo", opp.processo],
                      ["Unidade compradora (UASG)", opp.unidade_codigo], ["Nº da compra (Compras.gov)", opp.numero_compra ? `${String(opp.numero_compra).padStart(5, "0")}/${opp.ano_compra}` : null],
                      ["Sistema de origem", opp.sistema_origem],
                      ["Nº compra", opp.numero_compra], ["Modalidade", opp.modalidade_nome],
                      ["Situação", opp.situacao_nome], ["Amparo legal", opp.amparo_legal],
                      ["UF / Município", `${opp.uf || "—"} / ${opp.municipio || "—"}`],
                      ["Valor estimado", brl(opp.valor_estimado)],
                      ["Publicação", fmtDate(opp.data_publicacao)],
                      ["Encerramento proposta", fmtDateTime(opp.data_encerramento_proposta)],
                    ].map(([l, v]) => (
                      <div key={l} className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{l}</div>
                        <div className="mt-0.5 break-words text-sm font-medium text-slate-800">{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Objeto</div>
                    <p className="text-sm text-slate-700">{opp.objeto}</p>
                    {opp.informacao_complementar && <p className="mt-2 text-sm text-slate-500">{opp.informacao_complementar}</p>}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Por que apareceu (aderência {opp.aderencia}%)</div>
                    <ul className="space-y-1.5">
                      {(opp.aderencia_reasons || []).map((r, i) => (
                        <li key={i} className={`flex items-start gap-2 text-sm ${r.ok ? "text-slate-700" : "text-rose-600"}`}>
                          <span>{r.ok ? "✓" : "✗"}</span><span>{r.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {opp.link_oficial && (
                    <a href={opp.link_oficial} target="_blank" rel="noreferrer" data-testid="drawer-link-oficial"
                      className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 text-sm font-semibold text-sky-700 hover:bg-sky-50">
                      <ExternalLink className="h-4 w-4" /> Abrir edital no PNCP
                    </a>
                  )}
                  {opp.link_sistema_origem && (
                    <a href={opp.link_sistema_origem} target="_blank" rel="noreferrer" data-testid="drawer-link-origem"
                      className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 text-sm font-semibold text-sky-700 hover:bg-sky-50">
                      <ExternalLink className="h-4 w-4" /> Abrir no {opp.sistema_origem || "sistema de origem"}
                    </a>
                  )}
                  <div className="flex gap-2 pt-1">
                    <button data-testid="drawer-save-btn" onClick={() => patch({ saved: !opp.saved })} className={`flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-semibold ${opp.saved ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                      <Bookmark className="h-4 w-4" /> {opp.saved ? "Salva" : "Salvar"}
                    </button>
                    <button data-testid="drawer-favorite-btn" onClick={() => patch({ stage: "FAVORITA" })} className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      <Star className="h-4 w-4" /> Favoritar
                    </button>
                    <button data-testid="drawer-pipeline-btn" onClick={() => patch({ stage: "ANALISAR" })} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-sky-600 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
                      <Kanban className="h-4 w-4" /> Para pipeline
                    </button>
                  </div>
                  <button data-testid="drawer-participar-btn" onClick={participar} disabled={participando}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-sky-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition-transform hover:scale-[1.01] disabled:opacity-60">
                    {participando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Rocket className="h-5 w-5" />}
                    QUERO PARTICIPAR DESSA LICITAÇÃO
                  </button>
                  <p className="text-center text-[11px] text-slate-400">Abre o ambiente de trabalho da licitação — não altera o status do pipeline.</p>
                </div>
              )}

              {tab === "itens" && (
                <div>
                  {loadingItens ? (
                    <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>
                  ) : !itens || itens.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                      Nenhum item carregado. <button className="font-semibold text-sky-600" onClick={loadItens}>Carregar itens do PNCP</button>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                          <tr>
                            <th className="p-2">#</th><th className="p-2">Descrição</th>
                            <th className="p-2 text-right">Qtd</th><th className="p-2">Un.</th>
                            <th className="p-2 text-right">Vlr unit.</th><th className="p-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {itens.map((it, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="p-2 text-slate-400">{it.numero_item}</td>
                              <td className="p-2 text-slate-700">{it.descricao}</td>
                              <td className="p-2 text-right font-mono">{it.quantidade}</td>
                              <td className="p-2 text-slate-500">{it.unidade}</td>
                              <td className="p-2 text-right font-mono">{brl(it.valor_unitario)}</td>
                              <td className="p-2 text-right font-mono font-semibold">{brl(it.valor_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {tab === "gestao" && (
                <div className="space-y-4">
                  {opp.stage === "GANHA" && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                        <Trophy className="h-4 w-4" /> Licitação ganha
                      </div>
                      {opp.converted ? (
                        <p className="mt-2 text-sm text-emerald-700">
                          Convertida em venda. Contrato <strong>{opp.contrato}</strong> e conta a receber criada no Financeiro.
                        </p>
                      ) : (
                        <>
                          <p className="mt-1 text-xs text-emerald-700">Gere contrato, faturamento e conta a receber automaticamente.</p>
                          <button data-testid="converter-venda-btn" onClick={converter} disabled={converting}
                            className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                            {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                            Converter em venda / contrato
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Etapa do pipeline</label>
                    <select data-testid="drawer-stage-select" value={opp.stage} onChange={(e) => patch({ stage: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Valor proposto</label>
                      <input data-testid="drawer-valor-proposto" type="number" defaultValue={opp.valor_proposto ?? ""} onBlur={(e) => patch({ valor_proposto: parseFloat(e.target.value) || null })} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Custo</label>
                      <input type="number" defaultValue={opp.custo ?? ""} onBlur={(e) => patch({ custo: parseFloat(e.target.value) || null })} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Responsável</label>
                      <input defaultValue={opp.responsavel ?? ""} onBlur={(e) => patch({ responsavel: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Margem %</label>
                      <input type="number" defaultValue={opp.margem ?? ""} onBlur={(e) => patch({ margem: parseFloat(e.target.value) || null })} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                    </div>
                  </div>

                  {opp.stage === "PERDIDA" && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                      <label className="mb-1 block text-xs font-semibold text-rose-700">Motivo da perda</label>
                      <input defaultValue={opp.loss_reason ?? ""} onBlur={(e) => patch({ loss_reason: e.target.value })} placeholder="Ex: Concorrente com preço menor" className="w-full rounded-lg border border-rose-200 px-2 py-1.5 text-sm" />
                    </div>
                  )}

                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><ListChecks className="h-4 w-4 text-sky-600" /> Checklist</div>
                    {(!opp.checklist || opp.checklist.length === 0) ? (
                      <button data-testid="drawer-create-checklist" onClick={() => patch({ saved: true })} className="text-sm font-semibold text-sky-600">Gerar checklist padrão</button>
                    ) : (
                      <div className="space-y-1.5">
                        {opp.checklist.map((c, i) => (
                          <label key={i} className="flex cursor-pointer items-center gap-2 text-sm">
                            <input type="checkbox" checked={c.done} onChange={() => toggleCheck(i)} className="h-4 w-4 rounded border-slate-300 text-sky-600" />
                            <span className={c.done ? "text-slate-400 line-through" : "text-slate-700"}>{c.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Observações</label>
                    <textarea defaultValue={opp.observacoes ?? ""} onBlur={(e) => patch({ observacoes: e.target.value })} rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                  </div>
                </div>
              )}

              {tab === "ia" && (
                <div className="space-y-4">
                  <button data-testid="drawer-analyze-btn" onClick={analyze} disabled={analyzing}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-emerald-600 py-3 text-sm font-semibold text-white disabled:opacity-60">
                    {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {opp.ai_analysis ? "🔎 Analisar novamente" : "🔎 Análise Profunda (IA lê o edital)"}
                  </button>
                  {opp.ai_analysis ? (
                    <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700" data-testid="drawer-ai-result">
                      {opp.ai_analysis}
                    </div>
                  ) : (
                    <p className="text-center text-sm text-slate-400">A IA resume o objeto, identifica produtos compatíveis, documentos exigidos, prazos e riscos — com base apenas nos dados do PNCP.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
